import * as React from "react";

import { type Board, type UpdateBoardRequest } from "../BoardRecord";
import {
  createBoardWriteQueue,
  type BoardWriteQueue,
} from "../boardWriteQueue";

import { useUpdateBoard } from "./useUpdateBoard";

/** Sends one update for one board and resolves with the record the server stored. */
type Send = (request: {
  boardId: string;
  orgId: string;
  updates: UpdateBoardRequest;
}) => Promise<Board>;

/**
 * How the board on screen is written: its write queue without `dispose`, which
 * only `useBoardWriter` calls.
 */
export type BoardWriter = Omit<BoardWriteQueue, "dispose">;

/**
 * A writer for the loaded board, or `undefined` while no board is loaded.
 *
 * Opening a different board, or the same board in a different org, gets a new
 * writer, so a response for the board a person just left is never applied to the
 * board they are looking at.
 *
 * `useUpdateBoard` writes each record the server returns into the board query's
 * cache unless the query's error is an access error, so when `loaded` is that
 * query's data it follows refetches and the writes of every `useUpdateBoard`
 * call in this tab for the same board and org. Passing that record to `save` or
 * `rename` as `displayedRecord` lets a write start from it while the queue keeps
 * the writes waiting in it and any conflict it holds; only the caller's `adopt`
 * ends a conflict.
 */
export function useBoardWriter(
  loaded: Board | undefined,
): BoardWriter | undefined {
  const updateBoard = useUpdateBoard();
  const active = React.useRef(true);
  React.useLayoutEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  // The queue outlives the render that built it, so it reaches the mutation
  // through a ref rather than capturing that render's `mutateAsync`.
  const send = React.useRef<Send>(updateBoard.mutateAsync);
  React.useEffect(
    function trackMutation() {
      send.current = updateBoard.mutateAsync;
    },
    [updateBoard.mutateAsync],
  );

  const [queue, setQueue] = React.useState<BoardWriteQueue | null>(() =>
    boardWriteQueueFor(loaded, send, active),
  );

  // Replace the queue while rendering when the loaded board or org differs,
  // rather than in an effect, so no render hands out a writer for the board
  // that just left the screen. Only the board id and org id are compared: a
  // record arriving from a refetch is the same board, and replacing the queue
  // for it would drop the writes waiting in it. A queue's record always names
  // the board and org it was created for.
  if (
    loaded?.board_id !== queue?.record.board_id ||
    loaded?.org_id !== queue?.record.org_id
  ) {
    queue?.dispose();
    setQueue(boardWriteQueueFor(loaded, send, active));
  }

  return queue ?? undefined;
}

function boardWriteQueueFor(
  loaded: Board | undefined,
  send: { current: Send },
  active: { current: boolean },
): BoardWriteQueue | null {
  if (loaded === undefined) {
    return null;
  }
  const { board_id: boardId, org_id: orgId } = loaded;
  return createBoardWriteQueue(
    loaded,
    (updates) => send.current({ boardId, orgId, updates }),
    () => active.current,
  );
}
